"use client";

// Static renderer for non-playing state - renders continuous text blocks with bracketed spans using WordRenderer
// This provides better performance by not rendering too many DOM elements on non-active state

import { useMemo } from "react";

import { WordRenderer, isBracketedPhrase } from "./WordRenderer";

export const StaticTranscriptRenderer = ({
  displayWords,
  className,
  compact,
  showMetadata,
}: {
  className?: string;
  compact?: boolean;
  displayWords: string[];
  showMetadata?: boolean;
}) => {
  const optimizedElements = useMemo(() => {
    const elements: React.ReactNode[] = [];
    let currentTextBlock = "";

    for (let i = 0; i < displayWords.length; i++) {
      const displayWord = displayWords[i];

      // Check if this word has brackets (using same logic as WordRenderer)
      const bracketMatch = isBracketedPhrase(displayWord);

      if (bracketMatch) {
        // Flush any accumulated text block first
        if (currentTextBlock.trim()) {
          elements.push(currentTextBlock);
          currentTextBlock = "";
        }

        // Use WordRenderer for consistent bracketed word styling
        elements.push(
          <WordRenderer
            compact={compact}
            highlighted={false}
            key={`bracketed-${i}`}
            showMetadata={showMetadata}
            word={displayWord}
            wordIndex={i}
          />,
        );
      } else {
        // Accumulate regular words into text blocks
        currentTextBlock += displayWord + (i < displayWords.length - 1 ? " " : "");
      }
    }

    // Flush any remaining text block
    if (currentTextBlock.trim()) {
      elements.push(currentTextBlock);
    }

    return elements;
  }, [displayWords, compact, showMetadata]);

  return <div className={className}>{optimizedElements}</div>;
};
